import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIcon from "@mui/icons-material/Phone";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SecurityIcon from "@mui/icons-material/Security";
import PsychologyIcon from "@mui/icons-material/Psychology";
import { useNavigate } from "react-router-dom";

const contacts = [
  {
    id: 1,
    label: "Emergency Services",
    numbers: ["999", "112"],
    description: "Police, Fire & Ambulance — free from any phone, any network",
    icon: <SecurityIcon sx={{ fontSize: 28 }} />,
    color: "#ef4444",
    tag: "Emergency",
  },
  {
    id: 2,
    label: "Kenya Red Cross",
    numbers: ["1199"],
    description: "Toll-free quick response to any emergency nationwide",
    icon: <FavoriteIcon sx={{ fontSize: 28 }} />,
    color: "#f97316",
    tag: "Emergency",
  },
  {
    id: 3,
    label: "E-Plus Ambulance",
    numbers: ["1514"],
    description: "Kenya Red Cross ambulance dispatch across Kenya",
    icon: <LocalHospitalIcon sx={{ fontSize: 28 }} />,
    color: "#f59e0b",
    tag: "Medical",
  },
  {
    id: 4,
    label: "Ministry of Health",
    numbers: ["719", "0732353535"],
    description: "Health emergencies, disease surveillance & guidance",
    icon: <LocalHospitalIcon sx={{ fontSize: 28 }} />,
    color: "#22d3ee",
    tag: "Medical",
  },
  {
    id: 5,
    label: "Befrienders Kenya",
    numbers: ["+254722178177"],
    description:
      "Mental health crisis support — someone is always here to listen",
    icon: <PsychologyIcon sx={{ fontSize: 28 }} />,
    color: "#4ade80",
    tag: "Mental Health",
  },
];

const tagColors = {
  Emergency: "#ef4444",
  Medical: "#22d3ee",
  "Mental Health": "#4ade80",
};

export default function Crisis() {
  const navigate = useNavigate();
  const [calling, setCalling] = useState(null);

  const handleCall = (number) => {
    setCalling(number);
    window.location.href = `tel:${number}`;
    setTimeout(() => setCalling(null), 2000);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0a1628, #0d1f3c, #0a2040)",
        px: { xs: 2, sm: 3 },
        py: 4,
        pb: 6,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ color: "#7fa8c8", mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h5"
          sx={{ color: "#e8f4f8", fontWeight: 700, fontSize: "1.3rem" }}
        >
          Crisis Resources
        </Typography>
      </Box>

      {/* Hero message */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, rgba(74,222,128,0.12), rgba(34,211,238,0.10))",
          border: "1px solid rgba(74,222,128,0.25)",
          borderRadius: 3,
          px: 3,
          py: 2.5,
          mb: 4,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: "#4ade80",
            fontWeight: 700,
            fontSize: "1.05rem",
            mb: 0.5,
          }}
        >
          You are not alone. 🌿
        </Typography>
        <Typography sx={{ color: "#7fa8c8", fontSize: "0.88rem", lineHeight: 1.6 }}>
          If you or someone you know is in danger or needs help,
          reach out immediately. These lines are available 24/7.
          Tap any number to call directly.
        </Typography>
      </Box>

      {/* Contact Cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {contacts.map((contact) => (
          <Card
            key={contact.id}
            sx={{
              background: "rgba(13, 31, 60, 0.85)",
              border: `1px solid rgba(${hexToRgb(contact.color)}, 0.25)`,
              borderRadius: 3,
              boxShadow: `0 0 18px rgba(${hexToRgb(contact.color)}, 0.08)`,
              transition: "all 0.2s ease",
              "&:hover": {
                border: `1px solid rgba(${hexToRgb(contact.color)}, 0.5)`,
                transform: "translateY(-2px)",
                boxShadow: `0 4px 24px rgba(${hexToRgb(contact.color)}, 0.15)`,
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              {/* Top row: icon + label + tag */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ color: contact.color }}>{contact.icon}</Box>
                  <Typography
                    sx={{
                      color: "#e8f4f8",
                      fontWeight: 700,
                      fontSize: "1rem",
                    }}
                  >
                    {contact.label}
                  </Typography>
                </Box>
                <Chip
                  label={contact.tag}
                  size="small"
                  sx={{
                    background: `rgba(${hexToRgb(tagColors[contact.tag])}, 0.15)`,
                    color: tagColors[contact.tag],
                    border: `1px solid rgba(${hexToRgb(tagColors[contact.tag])}, 0.3)`,
                    fontSize: "0.7rem",
                    height: 22,
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* Description */}
              <Typography
                sx={{
                  color: "#7fa8c8",
                  fontSize: "0.83rem",
                  mb: 2,
                  lineHeight: 1.5,
                }}
              >
                {contact.description}
              </Typography>

              {/* Call Buttons */}
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {contact.numbers.map((num) => (
                  <Button
                    key={num}
                    variant="contained"
                    size="small"
                    startIcon={<PhoneIcon sx={{ fontSize: "0.9rem" }} />}
                    onClick={() => handleCall(num)}
                    sx={{
                      background:
                        calling === num
                          ? `rgba(${hexToRgb(contact.color)}, 0.4)`
                          : `rgba(${hexToRgb(contact.color)}, 0.15)`,
                      color: contact.color,
                      border: `1px solid rgba(${hexToRgb(contact.color)}, 0.4)`,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      px: 2,
                      py: 0.7,
                      boxShadow: "none",
                      "&:hover": {
                        background: `rgba(${hexToRgb(contact.color)}, 0.3)`,
                        boxShadow: `0 0 12px rgba(${hexToRgb(contact.color)}, 0.3)`,
                      },
                    }}
                  >
                    {formatNumber(num)}
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Footer note */}
      <Typography
        sx={{
          color: "#3a5a7a",
          fontSize: "0.78rem",
          textAlign: "center",
          mt: 4,
          lineHeight: 1.6,
        }}
      >
        Imara is not a crisis service. In a life-threatening emergency,
        always call 999 or 112 first.
      </Typography>
    </Box>
  );
}

// Helper: convert hex to rgb string for rgba()
function hexToRgb(hex) {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

// Helper: format phone number for display
function formatNumber(num) {
  if (num.length <= 4) return num;
  return num;
}